import React, { useState, useEffect } from 'react';
import './PasswordGate.css';

const HASH = 'e1851525b1aca2e457656a6a0fd3e6aedc1082e39d8bcf24ff0cba3d0ba7642d';
const SESSION_KEY = 'tc_access';

async function sha256(message: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

interface PasswordGateProps {
  children: React.ReactNode;
}

const PasswordGate: React.FC<PasswordGateProps> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === 'true') {
      setAuthenticated(true);
    }
    setChecking(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(false);
    const hashed = await sha256(password);
    if (hashed === HASH) {
      sessionStorage.setItem(SESSION_KEY, 'true');
      setAuthenticated(true);
    } else {
      setError(true);
      setPassword('');
    }
  };

  if (checking) return null;
  if (authenticated) return <>{children}</>;

  return (
    <div className="gate-overlay">
      <div className="gate-card">
        <div className="gate-brand">
          <img src="/logo192.png" alt="TableChat" width="64" height="64" />
          <h1 className="gate-title">TableChat<span className="gate-tm">&trade;</span></h1>
        </div>

        <div className="gate-message">
          <p>
            Due to legal requirements that some U.S. States are imposing, TableChat is no longer
            publicly available for viewing. Please contact{' '}
            <a href="mailto:Support@Tablechat.io">Support@Tablechat.io</a> for an
            opportunity to view our product and services.
          </p>
          <p className="gate-thanks">Thank you.</p>
        </div>

        <form className="gate-form" onSubmit={handleSubmit}>
          <div className="gate-input-group">
            <div className="gate-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(false); }}
                placeholder="Enter access code"
                autoFocus
                autoComplete="off"
                className={error ? 'gate-input gate-input-error' : 'gate-input'}
              />
              <button
                type="button"
                className="gate-toggle-visibility"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide access code' : 'Show access code'}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            <button type="submit" className="gate-submit">
              Enter
            </button>
          </div>
          {error && <p className="gate-error">Invalid access code</p>}
        </form>

        <div className="gate-footer">
          <p>&copy; {new Date().getFullYear()} TableChat&trade;. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PasswordGate;
